import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FirebaseContext, api } from "common";
import { push, ref, set, update } from "firebase/database";
import { colors } from "../components/Theme/WebTheme";
import { FONT_FAMILY, SECONDORY_COLOR, labelText } from "../common/sharedFunctions";

const USER_FIELDS = [
  "id",
  "firstName",
  "lastName",
  "email",
  "mobile",
  "usertype",
  "approved",
  "fleetadmin",
  "driverActiveStatus",
  "carType",
  "walletBalance",
  "referralId"
];

const DATASETS = {
  customers: { path: "users", usertype: "customer", fields: USER_FIELDS },
  drivers: { path: "users", usertype: "driver", fields: USER_FIELDS },
  fleetadmins: { path: "users", usertype: "fleetadmin", fields: USER_FIELDS },
  admins: { path: "users", usertype: "admin", fields: USER_FIELDS },
  container_yards: { path: "container_yards", fields: ["id", "name", "shippingLineName", "region", "location", "availableSlots", "totalSlots", "latitude", "longitude", "geofenceRadiusMeters", "active"] },
  vehicle_types: {
    path: "cartypes",
    fields: [
      "id",
      "name",
      "pos",
      "image",
      "active",
      "base_fare",
      "rate_per_unit_distance",
      "rate_per_hour",
      "min_fare",
      "convenience_fees",
      "convenience_fee_type",
      "fleet_admin_fee"
    ]
  },
  complaints: { path: "complain", fields: ["id", "firstName", "lastName", "role", "email", "mobile", "subject", "body", "check", "complainDate", "processDate"] }
};

const FALLBACK_LABELS = {
  data_import_export: "Import / Export",
  import_export_help: "Export current database rows as CSV or JSON, download a CSV template, or import CSV/JSON updates into Firebase.",
  customer_import_hybrid_warning: "Customer import is disabled because customers must be created and linked through the Hybrid Client workflow. Customer export remains available.",
  select_table: "Select Table",
  customers: "Customers",
  drivers: "Drivers",
  fleetadmins: "Fleet Admins / Logistics Companies",
  admins: "Admins",
  container_yards_title: "Container Yards",
  vehicle_types: "Vehicle Types",
  complaints: "Complaints",
  download_template: "Download Template",
  export_csv: "Export CSV",
  export_json: "Export JSON",
  import_file: "Import File",
  importing: "Importing...",
  records: "Records",
  import_update_rule: "Rows with an id update that Firebase record. Rows without an id create a new record.",
  user_import_auth_warning: "User import updates the Firebase Realtime Database profile first. Use Send Password Setup Links to create login accounts and send setup emails.",
  import_no_records: "No records found in the selected file.",
  import_failed: "Import failed. Please check the file format.",
  import_complete: "Import complete",
  updated: "updated",
  created: "created",
  send_password_setup_links: "Send Password Setup Links",
  send_password_setup_help: "Creates missing Firebase Authentication accounts for imported users, then emails each user a secure link to set their own password.",
  send_password_failed: "Unable to send password setup links. Deploy the Cloud Function and check SMTP settings.",
  send_password_network_error: "Unable to reach the password setup Cloud Function. Deploy functions first, then try again.",
  password_links_sent: "Password setup links sent",
  sent: "sent",
  failed: "failed",
  sending: "Sending...",
  users_with_email: "users with email",
  auth_created_link_sent: "Auth account created and link sent",
  link_sent: "Link sent",
  not_logged_in: "Not logged in",
  users_table: "Users",
  user_name: "User Name",
  user_type: "User Type",
  email_address: "Email Address",
  contact_number: "Mobile Number / Contact Number",
  password_reset: "Password Reset",
  send_password_reset: "Send Password Reset",
  no_users_found: "No users found",
  password_link_sent_one: "Password setup link sent to selected user"
};

const label = (t, key) => {
  return labelText(t, key, FALLBACK_LABELS[key]);
};

const cleanCsvValue = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadFile = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let value = "";
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];
    if (quoted) {
      if (char === '"' && next === '"') {
        value += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        value += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(value);
      value = "";
    } else if (char === "\n") {
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else if (char !== "\r") {
      value += char;
    }
  }

  row.push(value);
  rows.push(row);
  const headers = (rows.shift() || []).map((item) => item.trim());
  return rows
    .filter((items) => items.some((item) => item !== ""))
    .map((items) => headers.reduce((obj, header, index) => ({ ...obj, [header]: items[index] || "" }), {}));
};

const parseImportValue = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "") return "";
  return value;
};

const NUMERIC_FIELDS = [
  "availableSlots",
  "totalSlots",
  "pos",
  "base_fare",
  "rate_per_unit_distance",
  "rate_per_hour",
  "min_fare",
  "convenience_fees",
  "fleet_admin_fee",
  "walletBalance",
  "complainDate",
  "processDate"
];

const cleanRecordForFirebase = (record, config) => {
  const cleaned = {};
  Object.keys(record).forEach((key) => {
    if (key && key !== "id" && key !== "tableData" && key !== "location") {
      const value = typeof record[key] === "string" ? parseImportValue(record[key].trim()) : record[key];
      cleaned[key] = NUMERIC_FIELDS.includes(key) && value !== "" && !isNaN(Number(value)) ? Number(value) : value;
    }
  });
  if (config.usertype) {
    cleaned.usertype = config.usertype;
  }
  return cleaned;
};

const getExportHeaders = (records, config) => {
  const headers = [...config.fields];
  records.forEach((record) => {
    Object.keys(record).forEach((key) => {
      if (!headers.includes(key) && key !== "tableData" && key !== "location") {
        headers.push(key);
      }
    });
  });
  return headers;
};

const toCsv = (records, config) => {
  const headers = getExportHeaders(records, config);
  const rows = records.map((record) => headers.map((header) => cleanCsvValue(record[header])).join(","));
  return [headers.join(","), ...rows].join("\n");
};

const safeRecordsForExport = (records, config) => {
  return records.map((record) => {
    const cleaned = {};
    getExportHeaders(records, config).forEach((field) => {
      if (field !== "tableData" && field !== "location") {
        cleaned[field] = record[field] === undefined ? "" : record[field];
      }
    });
    return cleaned;
  });
};

export default function DataImportExport() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const functionsBaseUrl = process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true"
    ? `http://127.0.0.1:5001/${firebase.config.projectId}/us-central1`
    : `https://us-central1-${firebase.config.projectId}.cloudfunctions.net`;
  const { fetchUsersOnce, fetchContainerYards, fetchCarTypes, fetchComplain } = api;
  const users = useSelector((state) => state.usersdata.staticusers || []);
  const yards = useSelector((state) => state.containeryards.yards || []);
  const cartypes = useSelector((state) => state.cartypes.cars || []);
  const complaints = useSelector((state) => state.complaindata.list || []);
  const settings = useSelector((state) => state.settingsdata.settings);
  const [datasetKey, setDatasetKey] = useState("customers");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordUserBusy, setPasswordUserBusy] = useState("");
  const [passwordResults, setPasswordResults] = useState([]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
    dispatch(fetchContainerYards());
    dispatch(fetchCarTypes());
    dispatch(fetchComplain());
  }, [dispatch, fetchUsersOnce, fetchContainerYards, fetchCarTypes, fetchComplain]);

  const records = useMemo(() => {
    if (datasetKey === "customers") return users.filter((user) => user.usertype === "customer");
    if (datasetKey === "drivers") return users.filter((user) => user.usertype === "driver");
    if (datasetKey === "fleetadmins") return users.filter((user) => user.usertype === "fleetadmin");
    if (datasetKey === "admins") return users.filter((user) => user.usertype === "admin");
    if (datasetKey === "container_yards") return yards;
    if (datasetKey === "vehicle_types") return cartypes;
    if (datasetKey === "complaints") return complaints;
    return [];
  }, [datasetKey, users, yards, cartypes, complaints]);

  const config = DATASETS[datasetKey];
  const datasetLabel = label(t, datasetKey);
  const isUserDataset = config.path === "users";
  const allUsers = useMemo(() => {
    return users
      .filter((user) => user && user.usertype)
      .sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [users]);

  const exportCsv = () => {
    downloadFile(toCsv(records, config), `${datasetKey}.csv`, "text/csv;charset=utf-8");
  };

  const exportJson = () => {
    downloadFile(JSON.stringify(safeRecordsForExport(records, config), null, 2), `${datasetKey}.json`, "application/json;charset=utf-8");
  };

  const exportTemplate = () => {
    downloadFile(config.fields.join(",") + "\n", `${datasetKey}-template.csv`, "text/csv;charset=utf-8");
  };

  const importRows = async (rows) => {
    const updates = {};
    let created = 0;
    let updated = 0;

    rows.forEach((row) => {
      const id = row.id ? String(row.id).trim() : "";
      const payload = cleanRecordForFirebase(row, config);
      if (id) {
        updates[`${config.path}/${id}`] = payload;
        updated++;
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(ref(firebase.database), updates);
    }

    for (const row of rows) {
      const id = row.id ? String(row.id).trim() : "";
      if (!id) {
        await set(push(ref(firebase.database, config.path)), cleanRecordForFirebase(row, config));
        created++;
      }
    }

    return { created, updated };
  };

  const handleImport = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    if (datasetKey === "customers") {
      setMessage("");
      setError(label(t, "customer_import_hybrid_warning"));
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");
    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      try {
        const text = loadEvent.target.result;
        const rows = file.name.toLowerCase().endsWith(".json") ? JSON.parse(text) : parseCsv(text);
        const normalizedRows = Array.isArray(rows) ? rows : Object.keys(rows || {}).map((id) => ({ id, ...rows[id] }));
        if (normalizedRows.length === 0) {
          throw new Error(label(t, "import_no_records"));
        }
        const result = await importRows(normalizedRows);
        setMessage(`${label(t, "import_complete")}: ${result.updated} ${label(t, "updated")}, ${result.created} ${label(t, "created")}`);
        dispatch(fetchUsersOnce());
        dispatch(fetchContainerYards());
        dispatch(fetchCarTypes());
        dispatch(fetchComplain());
      } catch (err) {
        setError(err && err.message ? err.message : label(t, "import_failed"));
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => {
      setError(label(t, "import_failed"));
      setBusy(false);
    };
    reader.readAsText(file);
  };

  const sendPasswordSetupLinks = async () => {
    if (!isUserDataset || records.length === 0) return;
    setPasswordBusy(true);
    setError("");
    setMessage("");
    setPasswordResults([]);
    try {
      const token = firebase.auth.currentUser ? await firebase.auth.currentUser.getIdToken() : null;
      if (!token) throw new Error(label(t, "not_logged_in"));
      const response = await fetch(`${functionsBaseUrl}/send_password_setup_links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          usertype: config.usertype,
          userIds: records.map((record) => record.id).filter(Boolean),
          baseUrl: settings && settings.CompanyWebsite ? settings.CompanyWebsite : window.location.origin
        })
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || label(t, "send_password_failed"));
      }
      setPasswordResults(result.results || []);
      setMessage(`${label(t, "password_links_sent")}: ${result.sent || 0} ${label(t, "sent")}, ${result.failed || 0} ${label(t, "failed")}`);
      dispatch(fetchUsersOnce());
    } catch (err) {
      const isNetworkError = err && err.message && err.message.toLowerCase().includes("failed to fetch");
      setError(isNetworkError ? label(t, "send_password_network_error") : (err && err.message ? err.message : label(t, "send_password_failed")));
    } finally {
      setPasswordBusy(false);
    }
  };

  const sendPasswordSetupLinkForUser = async (user) => {
    if (!user || !user.id || !user.email) return;
    setPasswordUserBusy(user.id);
    setError("");
    setMessage("");
    setPasswordResults([]);
    try {
      const token = firebase.auth.currentUser ? await firebase.auth.currentUser.getIdToken() : null;
      if (!token) throw new Error(label(t, "not_logged_in"));
      const response = await fetch(`${functionsBaseUrl}/send_password_setup_links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          usertype: user.usertype,
          userIds: [user.id],
          baseUrl: settings && settings.CompanyWebsite ? settings.CompanyWebsite : window.location.origin
        })
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || label(t, "send_password_failed"));
      }
      setPasswordResults(result.results || []);
      const failed = result.failed || 0;
      if (failed > 0) {
        const firstError = result.results && result.results.find((item) => !item.success);
        throw new Error(firstError && firstError.error ? firstError.error : label(t, "send_password_failed"));
      }
      setMessage(label(t, "password_link_sent_one"));
      dispatch(fetchUsersOnce());
    } catch (err) {
      const isNetworkError = err && err.message && err.message.toLowerCase().includes("failed to fetch");
      setError(isNetworkError ? label(t, "send_password_network_error") : (err && err.message ? err.message : label(t, "send_password_failed")));
    } finally {
      setPasswordUserBusy("");
    }
  };

  return (
    <Grid container spacing={2} sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
      <Grid item xs={12}>
        <Card sx={{ borderRadius: "8px", boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold", mb: 1 }}>
              {label(t, "data_import_export")}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: colors.CARD_LABEL, mb: 3 }}>
              {label(t, "import_export_help")}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="dataset-label">{label(t, "select_table")}</InputLabel>
                  <Select labelId="dataset-label" value={datasetKey} label={label(t, "select_table")} onChange={(e) => setDatasetKey(e.target.value)}>
                    <MenuItem value="customers">{label(t, "customers")}</MenuItem>
                    <MenuItem value="drivers">{label(t, "drivers")}</MenuItem>
                    <MenuItem value="fleetadmins">{label(t, "fleetadmins")}</MenuItem>
                    <MenuItem value="admins">{label(t, "admins")}</MenuItem>
                    <MenuItem value="container_yards">{label(t, "container_yards_title")}</MenuItem>
                    <MenuItem value="vehicle_types">{label(t, "vehicle_types")}</MenuItem>
                    <MenuItem value="complaints">{label(t, "complaints")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportTemplate}>
                    {label(t, "download_template")}
                  </Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>
                    {label(t, "export_csv")}
                  </Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportJson}>
                    {label(t, "export_json")}
                  </Button>
                  <Button variant="contained" component="label" startIcon={<UploadFileIcon />} disabled={busy || datasetKey === "customers"}>
                    {busy ? label(t, "importing") : label(t, "import_file")}
                    <input hidden type="file" accept=".csv,.json,application/json,text/csv" onChange={handleImport} />
                  </Button>
                </Stack>
              </Grid>
            </Grid>
            {datasetKey === "customers" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {label(t, "customer_import_hybrid_warning")}
              </Alert>
            )}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
                {datasetLabel}: {records.length} {label(t, "records")}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: colors.CARD_LABEL }}>
                {label(t, "import_update_rule")}
              </Typography>
              {config.path === "users" ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {label(t, "user_import_auth_warning")}
                </Alert>
              ) : null}
              {message ? <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert> : null}
              {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      {isUserDataset ? (
        <Grid item xs={12}>
          <Card sx={{ borderRadius: "8px", boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold", mb: 1 }}>
                {label(t, "send_password_setup_links")}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: colors.CARD_LABEL, mb: 2 }}>
                {label(t, "send_password_setup_help")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" startIcon={<EmailIcon />} disabled={passwordBusy || records.length === 0} onClick={sendPasswordSetupLinks}>
                  {passwordBusy ? label(t, "sending") : label(t, "send_password_setup_links")}
                </Button>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY }}>
                  {records.filter((record) => record.email).length} {label(t, "users_with_email")}
                </Typography>
              </Stack>
              {passwordResults.length > 0 ? (
                <Box sx={{ mt: 2, maxHeight: 260, overflow: "auto", border: `1px solid ${colors.BORDER_BACKGROUND}`, borderRadius: "8px" }}>
                  {passwordResults.map((item, index) => (
                    <Box
                      key={`${item.email}-${index}`}
                      sx={{
                        p: 1,
                        borderBottom: index === passwordResults.length - 1 ? "none" : `1px solid ${colors.BORDER_BACKGROUND}`,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, wordBreak: "break-all" }}>
                        {item.email}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: item.success ? colors.GREEN : colors.RED }}>
                        {item.success ? (item.createdAuth ? label(t, "auth_created_link_sent") : label(t, "link_sent")) : item.error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: "8px", boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold", mb: 2 }}>
              {label(t, "users_table")}
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{label(t, "user_name")}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{label(t, "user_type")}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{label(t, "email_address")}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{label(t, "contact_number")}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{label(t, "password_reset")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allUsers.length > 0 ? allUsers.map((user) => {
                    const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-";
                    return (
                      <TableRow key={user.id || user.uid || user.email}>
                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{userName}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{label(t, user.usertype) || user.usertype}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{user.email || "-"}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{user.mobile || "-"}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<EmailIcon />}
                            disabled={!user.email || !!passwordUserBusy || passwordBusy}
                            onClick={() => sendPasswordSetupLinkForUser(user)}
                          >
                            {passwordUserBusy === user.id ? label(t, "sending") : label(t, "send_password_reset")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ fontFamily: FONT_FAMILY, textAlign: "center" }}>
                        {label(t, "no_users_found")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
